import { Link, useLocation } from "react-router-dom";
import {
  Home,
  CheckSquare,
  CalendarCheck,
  Percent,
  Headset,
  MessagesSquare,
  UsersRound,
  Receipt,
  ShoppingCart,
  Package,
  Database,
  BellRing,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import React, { SyntheticEvent, useState } from "react";
import { SnackbarCloseReason } from "@mui/material";
import CustomSnackbar from "./CustomSnackbar";

type NavItem = {
  name: string;
  path: string;
  icon: React.ReactElement;
};

export const navItems: NavItem[] = [
  { name: "Dashboard", path: "/dashboard", icon: <Home size={18} /> },
  { name: "Users & Pets", path: "/users", icon: <UsersRound size={18} /> },
  { name: "Approvals", path: "/approvals", icon: <CheckSquare size={18} /> },
  { name: "Appointments", path: "/appointments", icon: <CalendarCheck size={18} /> },
  { name: "Products", path: "/products", icon: <Package size={18} /> },
  { name: "Product Orders", path: "/orders", icon: <ShoppingCart size={18} /> },
  { name: "Charge & Tax Codes", path: "/codes", icon: <Receipt size={18} /> },
  { name: "Review Moderation", path: "/reviews", icon: <MessagesSquare size={18} /> },
  { name: "Coupons", path: "/coupons", icon: <Percent size={18} /> },
  { name: "Notification Management", path: "/notifications", icon: <BellRing size={18} /> },
  { name: "Support", path: "/support", icon: <Headset size={18} /> },
  { name: "Masters Management", path: "/masters", icon: <Database size={18} /> },
  { name: "Settlements", path: "/settlements", icon: <Receipt size={18} /> },
  { name: "Refunds", path: "/refunds", icon: <RotateCcw size={18} color="#3b82f6" /> },
  { name: "SOS Emergency", path: "/sos", icon: <AlertTriangle size={18} color="#ef4444" /> },
];

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
};

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isDesktop: boolean;
}

const NavLinkItem = ({
  item,
  isActive,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  onClick?: () => void;
}) => (
  <Link
    to={item.path}
    onClick={onClick}
    className={`
      flex items-center gap-3 px-4 py-2 text-sm rounded-xl transition-all duration-300
      ${isActive
        ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg"
        : "text-gray-300 hover:bg-gray-800/70 hover:text-white"}
    `}
  >
    <span className={isActive ? "text-white" : "text-gray-400 group-hover:text-white"}>
      {item.icon}
    </span>
    <span className="truncate">{item.name}</span>
  </Link>
);

export default function Sidebar({ isOpen, setIsOpen, isDesktop }: SidebarProps) {
  const location = useLocation();
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (
    _event: Event | SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason !== "clickaway") {
      setSnackbar((prev) => ({ ...prev, open: false }));
    }
  };

  const isPathActive = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      {/* Overlay for mobile */}
      {!isDesktop && isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity"
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full bg-gray-900 text-white shadow-lg flex flex-col justify-between
          transition-transform duration-300 z-50
          ${isDesktop ? "w-48 translate-x-0" : isOpen ? "w-48 translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo / Header */}
        <div className="flex items-center justify-between h-14 border-b border-gray-800 px-4">
          <h1 className="text-lg font-bold tracking-wide select-none">PetSync 360</h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 mt-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900 px-1">
          {navItems.map((item) => (
            <NavLinkItem
              key={item.path}
              item={item}
              isActive={isPathActive(item.path)}
              onClick={() => !isDesktop && setIsOpen(false)}
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-800 text-xs text-gray-400 select-none flex flex-col items-center text-center">
          <div className="mb-1">Version 1.0.0</div>
          <div>© 2025 PetSync 360. All rights reserved.</div>
        </div>
      </aside>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </>
  );
}
